// StepCameraEvidence — Step 2 即時相機 (requires_camera=1).
// Spec: docs/specs/location-tasks.md §8.5.2.
//
// Strict getUserMedia (rear camera) — `<input type="file" capture>` is
// intentionally NOT used because it allows users to pick from the gallery
// on Android, defeating the "現場拍照" intent.

import React, { useEffect, useRef, useState } from "react";
import { Camera, Loader2, AlertTriangle, Trash2, X } from "lucide-react";
import { apiFetch } from "../../../lib/api-client.js";
import type { WizardCtx, WizardData } from "./types.js";
import { parseR2KeyList } from "./types.js";

interface Props {
  ctx: WizardCtx;
  data: WizardData;
  onComplete: () => void;
}

type Phase = "idle" | "starting" | "live" | "uploading" | "error";

export default function StepCameraEvidence({ ctx, data, onComplete }: Props): React.ReactElement {
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [keys, setKeys] = useState<string[]>(parseR2KeyList(data.submission.photo_r2_keys));

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => () => { streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);

  // Attach the active stream to the <video> element. We can't set
  // srcObject inside `start()` itself because the <video> is rendered
  // conditionally (only when phase === "live"), so videoRef is still
  // null while we're still in "starting". Effect runs after the
  // setPhase("live") commit, by which point the <video> is mounted
  // and the ref is populated. Without this you get a black frame
  // forever — the symptom user reported as 「拍照功能沒有畫面」.
  useEffect(() => {
    if (phase !== "live") return;
    const v = videoRef.current;
    const s = streamRef.current;
    if (!v || !s) return;
    if (v.srcObject !== s) v.srcObject = s;
    void v.play().catch(() => { /* iOS may reject autoplay; user gesture already happened via 「開啟相機拍攝」 so this is usually fine */ });
  }, [phase]);

  const start = async (): Promise<void> => {
    setError(null);
    setPhase("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      setPhase("live");
      // The video element is mounted by the setPhase("live") render; the
      // attach effect above wires srcObject + play once the ref settles.
    } catch (e) {
      setError((e as Error).name === "NotAllowedError" ? "需要相機權限。請在瀏覽器設定允許後再試。" : "無法開啟相機：" + (e as Error).message);
      setPhase("error");
    }
  };

  const capture = async (): Promise<void> => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 960;
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const drawCtx = canvas.getContext("2d");
    if (!drawCtx) { setError("無法擷取畫面"); return; }
    drawCtx.drawImage(v, 0, 0, w, h);
    setPhase("uploading");
    try {
      const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.85));
      if (!blob) throw new Error("toBlob_failed");
      const form = new FormData();
      form.set("file", new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" }));
      form.set("submission_id", data.submission.id);
      const r = await apiFetch(ctx.paths.uploadPhoto(), { method: "POST", body: form }, ctx.auth);
      const body = (await r.json().catch(() => ({}))) as { ok?: boolean; r2_key?: string; error?: string };
      if (!r.ok || !body.r2_key) {
        setError(body.error ?? `HTTP ${r.status}`);
        setPhase("live");
        return;
      }
      const next = [...keys, body.r2_key];
      const patch = await apiFetch(
        ctx.paths.patchSubmission(data.submission.id),
        { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify({ photo_r2_keys: next }) },
        ctx.auth,
      );
      if (!patch.ok) {
        const t = await patch.text().catch(() => "");
        setError(t || `HTTP ${patch.status}`);
        setPhase("live");
        return;
      }
      setKeys(next);
      setPhase("live");
      onComplete();
    } catch (e) {
      setError((e as Error).message);
      setPhase("error");
    }
  };

  const remove = async (key: string): Promise<void> => {
    const next = keys.filter((k) => k !== key);
    const r = await apiFetch(
      ctx.paths.patchSubmission(data.submission.id),
      { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify({ photo_r2_keys: next }) },
      ctx.auth,
    );
    if (r.ok) {
      setKeys(next);
      onComplete();
    }
  };

  const stopCamera = (): void => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setPhase("idle");
  };

  // Native-camera-app style full-screen overlay: video fills the viewport,
  // close button top-left, photo count chip top-right, large round shutter
  // bottom-centre. Renders OUTSIDE the normal step flow via fixed
  // positioning so the wizard's surrounding chrome doesn't squeeze the
  // viewfinder into a 4:3 letterbox. Only live + the uploading flash
  // belong here; the rest of the states stay inline below.
  const overlayActive = phase === "live" || phase === "uploading";
  return (
    <div className="space-y-3">
      {keys.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {keys.map((k) => (
            <div key={k} className="relative aspect-square rounded-lg overflow-hidden bg-bg-raised">
              <Thumb r2Key={k} ctx={ctx} />
              <button
                onClick={() => void remove(k)}
                className="absolute top-1 right-1 bg-black/70 text-white p-1 rounded-full"
                aria-label="刪除"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {overlayActive && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            playsInline
            muted
          />
          {/* Top bar — close + already-shot counter, sits on a faint
              gradient so it's readable over any frame content. */}
          <div className="relative z-10 flex items-center justify-between px-4 pt-[max(env(safe-area-inset-top),12px)] pb-3 bg-gradient-to-b from-black/60 to-transparent">
            <button
              type="button"
              onClick={stopCamera}
              disabled={phase === "uploading"}
              className="h-10 w-10 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center disabled:opacity-50"
              aria-label="關閉相機"
            >
              <X className="h-5 w-5" />
            </button>
            {keys.length > 0 && (
              <span className="rounded-full bg-black/40 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5">
                已拍 {keys.length} 張
              </span>
            )}
          </div>
          <div className="flex-1" />
          {/* Bottom bar — large round shutter, vertically lifted to clear
              the iOS home indicator. Inner disk pulses subtly while
              uploading so the operator knows the tap registered. */}
          <div className="relative z-10 pb-[max(env(safe-area-inset-bottom),24px)] pt-6 bg-gradient-to-t from-black/70 to-transparent flex items-center justify-center">
            <button
              type="button"
              onClick={() => void capture()}
              disabled={phase !== "live"}
              className="h-20 w-20 rounded-full border-4 border-white flex items-center justify-center disabled:opacity-60 active:scale-95 transition-transform"
              aria-label="拍攝"
            >
              <span
                className={
                  "block rounded-full bg-white " +
                  (phase === "uploading" ? "h-12 w-12 animate-pulse" : "h-14 w-14")
                }
              />
            </button>
          </div>
        </div>
      )}

      {phase === "starting" ? (
        <div className="flex items-center gap-2 text-sm text-text-secondary py-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          啟動相機中…
        </div>
      ) : !overlayActive && (
        <button onClick={() => void start()} className="block w-full h-12 rounded-xl bg-accent text-text-inverse font-semibold text-sm inline-flex items-center justify-center gap-1">
          <Camera className="h-4 w-4" /> {keys.length > 0 ? "再加一張" : "開啟相機拍攝"}
        </button>
      )}

      {error && (
        <p className="text-xs text-danger inline-flex items-start gap-1">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />{error}
        </p>
      )}
    </div>
  );
}

function Thumb({ r2Key, ctx }: { r2Key: string; ctx: WizardCtx }): React.ReactElement {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let revoke: string | null = null;
    void apiFetch(ctx.paths.evidenceUrl(r2Key), {}, ctx.auth)
      .then(async (r) => r.ok ? r.blob() : null)
      .then((blob) => {
        if (!blob) return;
        revoke = URL.createObjectURL(blob);
        setSrc(revoke);
      });
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
    // `ctx` is recreated each render; key by r2Key only (auth identity
    // doesn't change mid-wizard).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r2Key]);
  if (!src) return <div className="w-full h-full animate-pulse bg-bg-hover" />;
  return <img src={src} alt="" className="w-full h-full object-cover" />;
}
